import React, { useState } from 'react';

export default function G081ImportCleanup({ api, reports, requestScope, onRemoved, onWorking }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [site, setSite] = useState('');
  const [preview, setPreview] = useState(null);
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const candidates = reports.filter(row => row.reportType === '9188-6');
  const sites = [...new Set(candidates.map(row => row.location))].sort();
  const visible = candidates.filter(row => !site || row.location === site);
  const changeSelection = ids => {
    setSelected(ids);
    setPreview(null);
    setConfirmation('');
    setMessage('');
  };
  const run = async remove => {
    setBusy(true);
    onWorking(true);
    setMessage('');
    try {
      const res = await api.post(`/g081-imports/imports/9188-6/${remove ? 'cleanup' : 'cleanup-preview'}`, {
        ...requestScope, reportIds: remove ? preview.reportIds : selected,
        ...(remove ? { previewToken: preview.previewToken, confirmation } : {}),
      });
      if (remove) {
        changeSelection([]);
        setMessage(`Removed ${res.data.removed.reports} imports and ${res.data.removed.tasks} tasks. Ready for bulk re-import.`);
        await onRemoved();
      } else {
        setPreview(res.data);
        setConfirmation('');
      }
    } catch (err) {
      setMessage(err.response?.data?.message || 'Import cleanup failed. Refresh and preview again.');
      setPreview(null);
      setConfirmation('');
    } finally {
      setBusy(false);
      onWorking(false);
    }
  };
  return <section className="g081-import-cleanup">
    <button type="button" disabled={busy} onClick={() => setOpen(!open)}>{open ? 'Hide cleanup' : 'Bulk remove 9188-6 imports'}</button>
    {open ? <>
      <p>Permanently remove selected 9188-6 imports from history and their imported tasks, including cancelled tasks. Then bulk re-import your corrected reports.</p>
      <label>Site <select value={site} disabled={busy} onChange={event => setSite(event.target.value)}>
        <option value="">All sites in this view</option>
        {sites.map(value => <option key={value}>{value}</option>)}
      </select></label>
      <div className="g081-import-cleanup__actions">
        <button type="button" disabled={busy || !visible.length} onClick={() => changeSelection([...new Set([...selected, ...visible.map(row => row.id)])])}>Select all listed imports</button>
        <button type="button" disabled={busy || !selected.length} onClick={() => changeSelection([])}>Clear selection</button>
        <span>{selected.length} selected across all sites</span>
      </div>
      <div className="g081-import-cleanup__reports">
        {visible.map(row => <label key={row.id}>
          <input type="checkbox" disabled={busy} checked={selected.includes(row.id)} onChange={event => changeSelection(event.target.checked ? [...selected, row.id] : selected.filter(id => id !== row.id))} />
          <span><strong>{row.location} | {row.originalFilename}</strong><br />{row.status} | Uploaded {row.uploadedAt ? new Date(row.uploadedAt).toLocaleString() : 'date unknown'}</span>
        </label>)}
        {!visible.length ? <p>No 9188-6 imports at this site.</p> : null}
      </div>
      <button type="button" disabled={busy || !selected.length} onClick={() => run(false)}>{busy ? 'Working…' : 'Preview removal'}</button>
      {preview ? <div className="g081-import-cleanup__preview">
        <p><strong>{preview.counts.reports} imports · {preview.counts.tasks} tasks · {preview.counts.tails} tails</strong></p>
        <p>This deletes {preview.counts.plannedTasks} tasks with planning or notes, {preview.counts.waiverAssignments} waiver assignments, and {preview.counts.workLinks} job links. It clears {preview.counts.supplyLinks} supply links and dependencies on {preview.counts.dependentTasks} retained tasks.</p>
        <p>FSID ETAR records remain; affected task links will need to be matched again. Aircraft, 8035 jobs, custom tasks, and reusable labor profiles remain. Re-importing does not restore deleted task planning or manual waiver assignments.</p>
        <details><summary>Review affected tails and tasks</summary>
          <ul>{preview.tasks.map(row => <li key={row.id}>{row.location} | {row.tail} | {row.noun}</li>)}</ul>
        </details>
        {preview.blockingReports.length ? <div role="alert">
          <p>These retained imports also reference the tasks. Include the batches below or review them before removal:</p>
          <ul>{preview.blockingReports.map(row => <li key={row.id}>{row.location} | {row.filename}</li>)}</ul>
        </div> : <>
          <label>Type REMOVE 9188-6 to confirm permanent removal
            <input aria-label="Confirm permanent import removal" value={confirmation} disabled={busy} onChange={event => setConfirmation(event.target.value)} />
          </label>
          <button type="button" disabled={busy || confirmation !== 'REMOVE 9188-6'} onClick={() => run(true)}>Permanently remove selected imports</button>
        </>}
      </div> : null}
    </> : null}
    {message ? <p role="status">{message}</p> : null}
  </section>;
}
